import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import QuizService from '../services/QuizService';
import FloatingMenu from '../components/FloatingMenu';
import GamificationService from '../services/GamificationService';
import BadgeUnlockModal from '../components/BadgeUnlockModal';
import { trackEvent, EVENTS } from '../services/analytics';
import { MASCOTS } from '../theme';

// Animation de succès "Génial!" avec pouces qui s'envolent
const SuccessAnimation = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="text-center animate-bounce">
        <div className="text-5xl mb-2">👍</div>
        <div 
          className="text-3xl font-bold text-[#4CAF50]"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          Génial !
        </div>
      </div>
      {/* Pouces qui s'envolent - Style app mobile */}
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="absolute text-2xl animate-ping"
          style={{
            left: `${30 + i * 20}%`,
            top: '40%',
            animationDuration: `${0.5 + i * 0.1}s`,
            animationDelay: `${i * 0.15}s`
          }}
        >
          👍
        </div>
      ))}
    </div>
  );
};

// Header avec bulle et mascotte
const QuizHeader = ({ showInfo = true, onInfoClick }) => {
  const [mascotUrl] = useState(() => {
    const quizMascots = MASCOTS.quiz || [MASCOTS.standing];
    return quizMascots[Math.floor(Math.random() * quizMascots.length)] || MASCOTS.fallback;
  });

  return (
    <div className="flex justify-between items-start mb-4">
      {/* Bulle "Dis Maman !" */}
      <Link to="/chat" className="relative">
        <svg 
          viewBox="0 0 200 80" 
          className="w-[140px] sm:w-[160px] h-auto"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
        >
          <path 
            d="M 15,40 C 15,15 35,5 70,5 C 110,3 150,8 175,18 C 192,25 197,40 192,55 C 188,68 165,75 130,78 C 95,81 55,78 30,68 L 10,82 L 20,65 C 13,60 15,50 15,40 Z"
            fill="white"
            stroke="#374151"
            strokeWidth="2.5"
          />
        </svg>
        <p 
          className="absolute top-1/2 left-1/2 text-xl font-bold text-[#333333] whitespace-nowrap"
          style={{ 
            fontFamily: '"Caveat", cursive',
            transform: 'translate(-52%, -50%) rotate(-1deg)'
          }}
        >
          Dis Maman !
        </p>
      </Link>

      {/* Info button + Mascotte */}
      <div className="flex items-start gap-2">
        {showInfo && onInfoClick && (
          <button
            onClick={onInfoClick}
            className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow-md text-[#4A90A4] font-bold"
          >
            i
          </button>
        )}
        <img 
          src={mascotUrl}
          alt="Mascotte"
          className="h-[100px] sm:h-[120px] object-contain"
          style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}
          onError={(e) => { e.target.src = MASCOTS.fallback; }}
        />
      </div>
    </div>
  );
};

// Composant carte de question - Style app mobile
const QuestionCard = ({ 
  question, 
  answers, 
  correctIndex,
  conversationId,
  questionId,
  revisionStage = 0,
  onAnswered,
  onDelete,
  questionNumber,
  totalQuestions,
  onNext,
  isLast,
  isPremium,
  childName,
  onNavigateSubscription
}) => {
  const [showFinishUpgrade, setShowFinishUpgrade] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [showRetryHint, setShowRetryHint] = useState(false);
  const [currentRevisionStage, setCurrentRevisionStage] = useState(revisionStage);
  const navigate = useNavigate();

  // Validation 1-clic (comme app mobile)
  const handleSelectAnswer = (index) => {
    if (isRevealed) return;
    
    setSelectedAnswer(index);
    setShowRetryHint(false);
    
    const isCorrect = index === correctIndex;
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (isCorrect) {
      setIsRevealed(true);
      setShowSuccess(true);
      // Incrémenter le stage de révision localement pour l'affichage
      const newStage = currentRevisionStage + 1;
      setCurrentRevisionStage(newStage);
      onAnswered(true, newAttempts, newStage >= 5); // Passer si "acquis"
    } else {
      if (newAttempts >= 2) {
        // 2ème erreur: révéler la bonne réponse (stage ne change pas)
        setIsRevealed(true);
        onAnswered(false, newAttempts, false);
      } else {
        // 1ère erreur: shake et permettre de réessayer
        setShake(true);
        setShowRetryHint(true);
        setTimeout(() => {
          setShake(false);
          setSelectedAnswer(null);
        }, 500);
      }
    }
  };

  const handleReadAnswer = () => {
    if (conversationId) {
      navigate(`/history?highlight=${conversationId}`);
    }
  };

  const handleDeleteQuestion = () => {
    if (window.confirm('Supprimer cette question définitivement ?')) {
      onDelete(questionId);
    }
  };

  return (
    <div className="relative">
      {showSuccess && (
        <SuccessAnimation onComplete={() => setShowSuccess(false)} />
      )}
      
      {/* Header du quiz avec compteur et progress dots */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 mb-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
        >
          ✕
        </button>
        
        <span className="text-[#333333] font-medium">
          Question {questionNumber}/{totalQuestions}
        </span>
        
        {/* Progress dots - 5 points représentant les 5 révisions vers "acquis" */}
        <div className="flex gap-1.5">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i < currentRevisionStage ? 'bg-[#4CAF50]' : 'bg-[#E0E0E0]'
              }`}
              title={`Révision ${i + 1}/5`}
            />
          ))}
        </div>
        
        {/* Bouton supprimer */}
        <button
          onClick={handleDeleteQuestion}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500"
        >
          🗑️
        </button>
      </div>

      {/* Carte de question - Style app mobile avec shake animation */}
      <div 
        className={`bg-white rounded-3xl p-6 shadow-md mb-4 transition-transform ${
          shake ? 'animate-pulse' : ''
        }`}
        style={{ 
          transform: shake ? 'translateX(-5px)' : 'translateX(0)',
          transition: 'transform 0.1s ease-in-out'
        }}
      >
        <p className="text-lg text-[#333333] text-center leading-relaxed font-medium">
          {question}
        </p>
      </div>

      {/* Hint "Presque!" - Style app mobile */}
      {showRetryHint && (
        <div className="text-center mb-3">
          <span className="text-[#9B59B6] font-medium text-sm">
            Presque ! Essaie encore une fois 🤔
          </span>
        </div>
      )}

      {/* Boutons de réponse - Validation 1-clic */}
      <div className="space-y-3 mb-4">
        {answers.map((answer, index) => {
          let buttonStyle = 'bg-white border-gray-200 text-[#333333]';
          let icon = null;
          
          if (isRevealed) {
            if (index === correctIndex) {
              buttonStyle = 'bg-[#E8F5E9] border-[#4CAF50] text-[#2E7D32] font-semibold';
              icon = <span className="text-[#4CAF50] text-xl">✓</span>;
            } else if (index === selectedAnswer && selectedAnswer !== correctIndex) {
              buttonStyle = 'bg-[#FFF3E0] border-[#FFAB91] text-[#E64A19] opacity-60';
              icon = <span className="text-[#E64A19] text-xl">✗</span>;
            }
          } else if (attempts === 1 && index === selectedAnswer) {
            // Réponse incorrecte au 1er essai (estompée)
            buttonStyle = 'bg-[#FFF3E0] border-[#FFAB91] text-[#E64A19] opacity-60';
          }

          return (
            <button
              key={index}
              onClick={() => handleSelectAnswer(index)}
              disabled={isRevealed || (attempts === 1 && index === selectedAnswer)}
              className={`w-full p-4 rounded-2xl border-2 flex justify-between items-center
                transition-all duration-200 ${buttonStyle}
                ${!isRevealed && !(attempts === 1 && index === selectedAnswer) 
                  ? 'hover:border-[#4A90A4] hover:bg-gray-50 cursor-pointer active:scale-[0.98]' 
                  : 'cursor-default'}
              `}
            >
              <span className="text-left">{answer}</span>
              {icon}
            </button>
          );
        })}
      </div>

      {/* Actions après réponse */}
      {isRevealed && (
        <div className="space-y-3">
          {/* Badge "Sujet acquis!" si 5 révisions atteintes */}
          {currentRevisionStage >= 5 && (
            <div 
              className="p-3 rounded-xl text-center"
              style={{ 
                background: 'rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}
            >
              <span className="text-[#2E7D32] font-medium">
                ✨ Sujet acquis ! Cette question ne reviendra plus.
              </span>
            </div>
          )}

          {/* Bouton relire la réponse */}
          {conversationId && (
            <button
              onClick={handleReadAnswer}
              className="w-full py-3 rounded-full bg-white border-2 border-gray-200 
                text-[#666666] font-medium hover:bg-gray-50 transition-all"
            >
              📖 Relire la réponse
            </button>
          )}

          {/* Bannière upgrade sous chaque question (utilisateurs gratuits) */}
          {!isPremium && (
            <div 
              data-testid="quiz-question-upgrade-banner"
              className="rounded-2xl p-4"
              style={{ 
                background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 100%)',
                border: '1px solid #FDE68A'
              }}
            >
              <p className="text-gray-800 text-sm font-semibold mb-1" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>
                {childName 
                  ? `🌟 ${childName} a encore des questions !`
                  : `🌟 Votre enfant a encore des questions !`
                }
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-2">
                Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.
              </p>
              <button
                onClick={onNavigateSubscription}
                className="w-full py-2.5 rounded-full font-semibold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
              >
                Découvrir Explorateur
              </button>
            </div>
          )}

          {/* Bouton question suivante - Style violet app mobile */}
          <button
            onClick={() => {
              if (isLast && !isPremium) {
                setShowFinishUpgrade(true);
              } else {
                onNext();
              }
            }}
            className="w-full py-4 rounded-full text-white font-semibold text-lg shadow-md
              hover:opacity-90 transition-all active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
          >
            {isLast ? '\u2B50 Récolter les étoiles' : 'Question suivante \u2192'}
          </button>

          {/* Popup upgrade au clic "Terminer" pour les gratuits */}
          {showFinishUpgrade && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-5">
              <div className="bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl text-center">
                <div className="text-4xl mb-4">🎉</div>
                <h2 
                  className="text-2xl font-bold text-[#333] mb-3"
                  style={{ fontFamily: "'Caveat', cursive" }}
                >
                  {childName ? `Bravo ${childName} !` : 'Bravo !'}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {childName 
                    ? `${childName} a encore des questions ! Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.`
                    : `Votre enfant a encore des questions ! Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.`
                  }
                </p>
                <button
                  onClick={onNavigateSubscription}
                  data-testid="quiz-finish-upgrade-cta"
                  className="w-full py-3.5 rounded-full font-bold text-white text-base transition-all hover:opacity-90 active:scale-[0.98] mb-3"
                  style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                >
                  Découvrir Explorateur
                </button>
                <button
                  onClick={() => { setShowFinishUpgrade(false); onNext(); }}
                  className="text-gray-400 text-sm hover:text-gray-500 transition-colors"
                >
                  Pas maintenant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Page principale Quiz
const QuizPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api } = useAuth();
  const { isPremium, canAccessQuiz, isLoading: isMonetizationLoading } = useMonetization();
  
  const [step, setStep] = useState('select_child'); // select_child, quiz, archived, no_questions, completed
  const [children, setChildren] = useState([]);
  const [childCounts, setChildCounts] = useState({});
  const [selectedChild, setSelectedChild] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [archivedQuestions, setArchivedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [archivedFilter, setArchivedFilter] = useState('all');
  const [correctCount, setCorrectCount] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [harvestedStars, setHarvestedStars] = useState(null);
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [showStarHarvest, setShowStarHarvest] = useState(false);
  
  const quizService = QuizService.getInstance();
  
  useEffect(() => {
    if (api) {
      quizService.setApi(api);
    }
  }, [api]);

  const conversationId = searchParams.get('conversation');
  const childIdFromParams = searchParams.get('child');
  const isFromConversation = !!conversationId;

  // Charger les enfants
  const fetchChildren = useCallback(async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data.children || []);
      
      const counts = {};
      for (const child of response.data.children || []) {
        counts[child.id] = await quizService.getAvailableCountForChild(child.id);
      }
      setChildCounts(counts);
    } catch (error) {
      console.error('Erreur chargement enfants:', error);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (api) {
      fetchChildren();
    }
  }, [api, fetchChildren]);

  // Auto-sélection si on vient d'une conversation
  useEffect(() => {
    if (isFromConversation && childIdFromParams && children.length > 0) {
      const child = children.find(c => c.id === childIdFromParams);
      if (child) {
        handleSelectChild(child);
      }
    }
  }, [isFromConversation, childIdFromParams, children]);

  const handleSelectChild = async (child) => {
    setSelectedChild(child);
    setIsLoading(true);
    
    try {
      let fetchedQuestions;
      if (conversationId) {
        fetchedQuestions = await quizService.getQuestionsForConversation(conversationId);
      } else {
        fetchedQuestions = await quizService.getQuestionsForChild(child.id);
      }
      
      setQuestions(fetchedQuestions);
      setCurrentQuestionIndex(0);
      
      if (fetchedQuestions.length === 0) {
        setStep('no_questions');
      } else {
        setStep('quiz');
        setCorrectCount(0);
        setTotalAnswered(0);
        setHarvestedStars(null);
        trackEvent(EVENTS.QUIZ_STARTED, {
          child_id: selectedChild?.id,
          questions_count: fetchedQuestions.length
        });
      }
    } catch (error) {
      console.error('Erreur chargement questions:', error);
      setStep('no_questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionAnswered = async (isCorrect, attempts, isMastered = false) => {
    const currentQuestion = questions[currentQuestionIndex];
    try {
      await quizService.submitAnswer(currentQuestion.id, isCorrect, attempts);
      setTotalAnswered(prev => prev + 1);
      if (isCorrect) setCorrectCount(prev => prev + 1);
      
      if (isMastered) {
        console.log('Question acquise:', currentQuestion.id);
      }
    } catch (error) {
      console.error('Erreur soumission réponse:', error);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Last question — trigger star harvest
      handleHarvestStars();
    }
  };

  const handleHarvestStars = async () => {
    setIsHarvesting(true);
    setShowStarHarvest(true);
    try {
      const svc = GamificationService.getInstance();
      svc.setApi(api);
      const allCorrect = correctCount === totalAnswered && totalAnswered > 0;
      const convId = questions[0]?.conversation_id || conversationId || '';
      const childId = selectedChild?.id || children[0]?.id || '';
      
      const result = await svc.harvestStars(childId, convId, allCorrect);
      setHarvestedStars(result);
      
      // Dispatch stars earned to header
      window.dispatchEvent(new CustomEvent('stars-earned', {
        detail: { amount: result.stars_earned, newBalance: result.new_balance }
      }));
      
      trackEvent(EVENTS.QUIZ_COMPLETED, {
        correct: correctCount, total: totalAnswered,
        all_correct: allCorrect, child_id: childId
      });
      
      // Show harvest animation for 2.5s then go to completed
      setTimeout(() => {
        setShowStarHarvest(false);
        setStep('completed');
      }, 2500);
    } catch (error) {
      console.error('Erreur harvest stars:', error);
      setShowStarHarvest(false);
      setStep('completed');
    } finally {
      setIsHarvesting(false);
    }
  };


  const handleShareResult = async () => {
    const name = selectedChild?.name || 'Mon enfant';
    const score = `${correctCount}/${totalAnswered}`;
    const text = `${name} vient d'apprendre quelque chose avec Dis Maman ! ${score} bonnes réponses \u{1F31F} dismaman.com`;

    // Try to generate a share card via canvas
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, 600, 400);
      grad.addColorStop(0, '#B8D4E3');
      grad.addColorStop(0.5, '#9ECDC4');
      grad.addColorStop(1, '#B5DCAE');
      ctx.fillStyle = grad;
      ctx.roundRect(0, 0, 600, 400, 20);
      ctx.fill();

      // White card
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.roundRect(30, 30, 540, 340, 16);
      ctx.fill();

      // Star emoji
      ctx.font = '60px serif';
      ctx.textAlign = 'center';
      ctx.fillText('\u2B50', 300, 100);

      // Name
      ctx.fillStyle = '#333';
      ctx.font = 'bold 32px sans-serif';
      ctx.fillText(name, 300, 155);

      // Score
      ctx.fillStyle = '#4A90A4';
      ctx.font = 'bold 48px sans-serif';
      ctx.fillText(score, 300, 220);

      // Subtitle
      ctx.fillStyle = '#666';
      ctx.font = '18px sans-serif';
      ctx.fillText('bonnes réponses', 300, 250);

      // Footer
      ctx.fillStyle = '#999';
      ctx.font = '14px sans-serif';
      ctx.fillText('dismaman.com', 300, 340);

      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      const file = new File([blob], 'quiz-result.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ text, files: [file] });
        return;
      }
    } catch (e) {
      console.log('Canvas share fallback:', e);
    }

    // Fallback: text-only share
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Dis Maman !', text, url: 'https://dismaman.com' });
      } catch (e) { /* user cancelled */ }
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await api.delete(`/quiz/questions/${questionId}`);
      // Retirer la question de la liste
      const newQuestions = questions.filter(q => q.id !== questionId);
      setQuestions(newQuestions);
      
      if (newQuestions.length === 0) {
        setStep('no_questions');
      } else if (currentQuestionIndex >= newQuestions.length) {
        setCurrentQuestionIndex(newQuestions.length - 1);
      }
    } catch (error) {
      console.error('Erreur suppression question:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleViewArchived = async () => {
    setIsLoading(true);
    try {
      const archived = await quizService.getArchivedQuestions(selectedChild?.id || children[0]?.id);
      setArchivedQuestions(archived);
      setStep('archived');
    } catch (error) {
      console.error('Erreur chargement archives:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArchivedQuestion = async (questionId) => {
    if (window.confirm('Supprimer cette question définitivement ?')) {
      try {
        await api.delete(`/quiz/questions/${questionId}`);
        setArchivedQuestions(prev => prev.filter(q => q.id !== questionId));
      } catch (error) {
        console.error('Erreur suppression:', error);
      }
    }
  };

  // Attendre le chargement des données de monétisation avant de vérifier l'accès
  if (isMonetizationLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A90A4] mx-auto mb-4"></div>
          <p className="text-[#333333] text-lg font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  // Écran de blocage pour utilisateurs gratuits (seulement pour les révisions générales, pas pour le quiz d'une conversation)
  // Les utilisateurs gratuits peuvent accéder au quiz via "Lancer le quiz" depuis une conversation
  // mais pas via le menu "Les Petites Questions" (révisions générales)
  if (!canAccessQuiz && !isFromConversation) {
    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          <QuizHeader showInfo={false} />
          
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            {/* Icône cadenas */}
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(155, 89, 182, 0.15)' }}
            >
              <span className="text-5xl">🔒</span>
            </div>
            
            {/* Titre */}
            <h1 
              className="text-3xl font-bold text-center mb-4"
              style={{ fontFamily: "'Caveat', cursive", color: '#333333' }}
            >
              Les Petites Questions
            </h1>
            
            {/* Message */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 max-w-sm mx-auto shadow-lg text-center">
              <p className="text-gray-700 mb-4">
                Les révisions sont réservées aux abonnés <strong>Explorateur</strong> ou <strong>Famille</strong>.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Passez à un abonnement premium pour réviser les réponses avec votre enfant grâce à des quiz adaptés !
              </p>
              
              {/* Bouton upgrade */}
              <button
                onClick={() => navigate('/subscription')}
                className="w-full py-3 rounded-full text-white font-bold text-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
              >
                ✨ Découvrir les abonnements
              </button>
            </div>
          </div>
          
          <FloatingMenu />
        </div>
      </>
    );
  }

  // Écran de chargement
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A90A4] mx-auto mb-4"></div>
          <p className="text-[#333333] text-lg font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  // Écran de sélection d'enfant - Style app mobile
  if (step === 'select_child') {
    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          <QuizHeader showInfo={true} onInfoClick={() => setShowInfoModal(true)} />

          {/* Titre - Style app mobile */}
          <div className="text-center mb-6">
            <h1 
              className="text-3xl font-bold text-[#333333] mb-2"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              Les Petites Questions
            </h1>
            <p className="text-[#666666] text-sm px-6">
              Révise les réponses précédentes avec des questions adaptées !
            </p>
          </div>

          {/* Sous-titre */}
          <p 
            className="text-center text-[#333333] font-bold text-xl mb-6"
            style={{ fontFamily: "'Caveat', cursive" }}
          >
            Choisis un enfant
          </p>

          {/* Boutons enfants - Style pills comme StoryPage avec pastilles notification */}
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto mb-8">
            {children.map((child, index) => {
              const count = childCounts[child.id] || 0;
              return (
                <button
                  key={child.id}
                  onClick={() => handleSelectChild(child)}
                  className="relative px-6 py-3 rounded-full transition-all duration-200 border-2 
                    bg-white border-[rgba(91,191,189,0.3)] text-[#333333] 
                    hover:border-[#4A90A4] hover:shadow-lg hover:scale-105 active:scale-95"
                  style={{ 
                    fontFamily: "'Caveat', cursive", 
                    fontSize: '1.5rem'
                  }}
                >
                  {child.name}
                  
                  {/* Badge notification rouge - Style app mobile */}
                  {count > 0 && (
                    <span 
                      className="absolute -top-2 -right-2 bg-[#E74C3C] text-white 
                        rounded-full min-w-[24px] h-[24px] flex items-center justify-center
                        text-xs font-bold border-2 border-white shadow-md"
                      style={{ fontSize: '12px' }}
                    >
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {children.length === 0 && (
            <div className="text-center">
              <p className="text-[#666666] mb-4">Aucun enfant configuré</p>
              <button
                onClick={() => navigate('/children')}
                className="text-[#4A90A4] font-medium underline"
              >
                + Ajouter un enfant
              </button>
            </div>
          )}

          {/* Lien vers archives - Style app mobile (réservé aux abonnés premium) */}
          {children.length > 0 && canAccessQuiz && (
            <div className="text-center">
              <button
                onClick={handleViewArchived}
                className="text-[#4A90A4] font-medium underline"
              >
                📚 Sujets acquis
              </button>
            </div>
          )}

          <FloatingMenu />

          {/* Modal info */}
          {showInfoModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl">
                <h2 
                  className="text-2xl font-bold text-[#333333] mb-4 text-center"
                  style={{ fontFamily: "'Caveat', cursive" }}
                >
                  Comment ça marche ?
                </h2>
                <div className="space-y-3 text-[#666666] text-sm">
                  <p>🎯 Les questions sont générées à partir des conversations avec ton enfant.</p>
                  <p>📚 Chaque question doit être révisée 5 fois pour être considérée comme "acquise".</p>
                  <p>⏰ Les révisions sont espacées dans le temps pour une meilleure mémorisation.</p>
                  <p>✨ Une fois acquise, la question disparaît du quiz et va dans "Sujets acquis".</p>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="w-full mt-6 py-3 rounded-full text-white font-semibold"
                  style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #6BB5A2 100%)' }}
                >
                  J'ai compris !
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Écran quiz
  if (step === 'quiz') {
    const currentQuestion = questions[currentQuestionIndex];
    const isLast = currentQuestionIndex === questions.length - 1;

    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32 relative"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          {/* Mascotte en fond (opacity 0.25) */}
          <div 
            className="absolute bottom-10 right-0 w-40 h-40 opacity-25 pointer-events-none"
            style={{
              backgroundImage: `url(${MASCOTS.quiz?.[currentQuestionIndex % 5] || MASCOTS.fallback})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'bottom right'
            }}
          />

          <div className="max-w-lg mx-auto relative z-10">
            <QuestionCard
              key={currentQuestion.id}
              question={currentQuestion.question}
              answers={currentQuestion.answers}
              correctIndex={currentQuestion.correct_answer_index}
              conversationId={currentQuestion.conversation_id}
              questionId={currentQuestion.id}
              revisionStage={currentQuestion.revision_stage || 0}
              onAnswered={handleQuestionAnswered}
              onDelete={handleDeleteQuestion}
              questionNumber={currentQuestionIndex + 1}
              totalQuestions={questions.length}
              onNext={handleNextQuestion}
              isLast={isLast}
              isPremium={isPremium}
              childName={selectedChild?.name}
              onNavigateSubscription={() => navigate('/subscription')}
            />
          </div>

          {/* Star Harvest Animation Overlay */}
          {showStarHarvest && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="star-harvest-overlay">
              <div className="text-center">
                <div className="relative inline-block">
                  {[...Array(8)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute text-2xl"
                      style={{
                        left: '50%',
                        top: '50%',
                        animation: `starBurst 2s ease-out forwards`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    >
                      &#11088;
                    </span>
                  ))}
                  <span className="text-7xl relative z-10 inline-block animate-bounce">&#11088;</span>
                </div>
                <p className="text-3xl font-bold text-white mt-4" style={{ fontFamily: "'Caveat', cursive", textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                  +{harvestedStars?.stars_earned || 3} étoiles !
                </p>
              </div>
              <style>{`
                @keyframes starBurst {
                  0% { opacity: 1; transform: translate(-50%,-50%) scale(0.3); }
                  60% { opacity: 0.8; }
                  100% { opacity: 0; transform: translate(calc(-50% + ${Math.random()*60-30}px), calc(-50% - 100px)) scale(1.5); }
                }
              `}</style>
            </div>
          )}

          {/* Badge unlock modal */}
          {harvestedStars?.new_badges?.length > 0 && !showStarHarvest && (
            <BadgeUnlockModal
              badge={harvestedStars.new_badges[0]}
              onClose={() => setHarvestedStars(prev => ({...prev, new_badges: prev.new_badges.slice(1)}))}
            />
          )}

          <FloatingMenu />
        </div>
      </>
    );
  }

  // Écran archives (Sujets acquis)
  if (step === 'archived') {
    const filteredArchived = archivedFilter === 'all' 
      ? archivedQuestions 
      : archivedQuestions.filter(q => q.child_id === archivedFilter);

    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          {/* Header avec bouton retour */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setStep('select_child')}
              className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-md"
            >
              ←
            </button>
            <h1 
              className="text-2xl font-bold text-[#333333]"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              📚 Sujets acquis
            </h1>
          </div>

          {/* Filtres par enfant - Style app mobile */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setArchivedFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                archivedFilter === 'all'
                  ? 'bg-[#9B59B6] text-white'
                  : 'bg-white text-[#666666]'
              }`}
            >
              Tous
            </button>
            {children.map(child => (
              <button
                key={child.id}
                onClick={() => setArchivedFilter(child.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  archivedFilter === child.id
                    ? 'bg-[#9B59B6] text-white'
                    : 'bg-white text-[#666666]'
                }`}
              >
                {child.name}
              </button>
            ))}
          </div>

          {/* Liste des questions archivées */}
          <div className="space-y-3">
            {filteredArchived.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#666666]">Aucun sujet acquis pour le moment</p>
              </div>
            ) : (
              filteredArchived.map(q => (
                <div key={q.id} className="bg-white rounded-2xl p-4 shadow-sm flex justify-between items-start">
                  <div className="flex-1 pr-3">
                    <p className="text-[#333333] font-medium">{q.question}</p>
                    <p className="text-xs text-[#999999] mt-1">
                      ✅ Acquis • {q.revision_stage || 5} révisions
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteArchivedQuestion(q.id)}
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center 
                      text-gray-400 hover:bg-red-50 hover:text-red-500 flex-shrink-0"
                  >
                    🗑️
                  </button>
                </div>
              ))
            )}
          </div>

          <FloatingMenu />
        </div>
      </>
    );
  }

  // Écran pas de questions
  if (step === 'no_questions') {
    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32 flex flex-col"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          <QuizHeader showInfo={false} />

          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="text-6xl mb-4">📭</div>
            <h1 
              className="text-2xl font-bold text-[#333333] mb-2"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              Pas encore de questions
            </h1>
            <p className="text-[#666666] mb-8">
              {selectedChild?.name || 'Ton enfant'} n'a pas encore de questions à réviser.<br/>
              Pose des questions pour en générer !
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="px-8 py-4 rounded-full text-white font-semibold shadow-lg"
              style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #6BB5A2 100%)' }}
            >
              💬 Poser une question
            </button>
            
            <button
              onClick={() => setStep('select_child')}
              className="mt-4 text-[#4A90A4] font-medium"
            >
              ← Choisir un autre enfant
            </button>
          </div>

          <FloatingMenu />
        </div>
      </>
    );
  }

  // Écran quiz terminé
  if (step === 'completed') {
    return (
      <>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}</style>
        <div 
          className="min-h-screen px-4 py-6 pb-32 flex flex-col items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
        >
          <div className="text-center max-w-sm mx-auto">
            <div className="text-6xl mb-4">&#11088;</div>
            <h1 
              className="text-3xl font-bold text-[#333333] mb-2"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              Bravo {selectedChild?.name} !
            </h1>

            {/* Stars earned summary */}
            {harvestedStars && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="quiz-stars-earned">
                <p className="text-amber-700 font-bold text-lg">
                  +{harvestedStars.stars_earned} étoiles récoltées
                </p>
                <p className="text-amber-500 text-sm">
                  {correctCount}/{totalAnswered} bonne{correctCount > 1 ? 's' : ''} réponse{correctCount > 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Push premium conditionnel — gratuits uniquement */}
            {!isPremium && (
              <div 
                data-testid="post-quiz-upgrade-banner"
                className="rounded-2xl p-5 mb-5 text-left"
                style={{ 
                  background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 100%)',
                  border: '1px solid #FDE68A'
                }}
              >
                <p className="text-gray-800 text-sm leading-relaxed mb-3">
                  &#127881; {selectedChild?.name || 'Votre enfant'} apprend vite ! Avec Explorateur, {selectedChild?.name ? `${selectedChild.name.toLowerCase() === selectedChild.name ? selectedChild.name : 'il'}` : 'il'} pourra tout retenir grâce aux révisions ludiques — et vous aurez de nouveaux moments comme celui-ci chaque jour.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  data-testid="post-quiz-upgrade-cta"
                  className="w-full py-3 rounded-full font-bold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                >
                  Découvrir Explorateur
                </button>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={() => navigate('/chat')}
                data-testid="quiz-completed-ask-more"
                className="w-full px-8 py-4 rounded-full text-white font-semibold shadow-lg"
                style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #6BB5A2 100%)' }}
              >
                Poser d'autres questions
              </button>

              {/* Bouton partage résultat */}
              <button
                onClick={() => handleShareResult()}
                data-testid="quiz-share-result"
                className="w-full px-8 py-3 rounded-full bg-white text-[#4A90A4] font-semibold shadow-md border border-[#4A90A4]/20 text-sm"
              >
                &#128228; Partager le résultat {selectedChild?.name ? `de ${selectedChild.name}` : ''}
              </button>

              <button
                onClick={() => {
                  setStep('select_child');
                  setSelectedChild(null);
                  fetchChildren();
                }}
                data-testid="quiz-completed-back"
                className="w-full px-8 py-3 rounded-full text-[#666] font-medium text-sm"
              >
                &#8592; Retour au quiz
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default QuizPage;
